"use client";

/**
 * Blog Reaction Widget
 *
 * Displays a "Was this article helpful?" prompt with Yes/No buttons.
 * Allows optional feedback when user clicks "No".
 */

import { useState } from "react";
import { getSessionId } from "@/lib/session";
import { ThumbsUp, ThumbsDown, Check } from "lucide-react";

const API_BASE = process.env.NEXT_PUBLIC_WEBSITE_JOBS_API;

interface BlogReactionProps {
  slug: string;
  className?: string;
}

export function BlogReaction({ slug, className = "" }: BlogReactionProps) {
  const [status, setStatus] = useState<
    "idle" | "submitting" | "submitted" | "error"
  >("idle");
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");

  const handleReaction = async (isHelpful: boolean) => {
    if (!API_BASE) {
      console.warn("WEBSITE_JOBS_API not configured");
      return;
    }

    const sessionId = getSessionId();
    if (!sessionId) return;

    setStatus("submitting");

    try {
      const response = await fetch(`${API_BASE}/blogs/${slug}/reaction/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          is_helpful: isHelpful,
          feedback: feedback || undefined,
        }),
      });

      if (response.ok) {
        setStatus("submitted");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  if (status === "submitted") {
    return (
      <div
        className={`border-t border-gray-200 pt-8 mt-12 text-center ${className}`}
      >
        <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-50 text-green-700 rounded-lg">
          <Check className="w-5 h-5" />
          <span className="font-medium">Thank you for your feedback!</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border-t border-gray-200 pt-8 mt-12 ${className}`}
    >
      <div className="text-center">
        <p className="text-lg text-gray-700 mb-6 font-medium">
          Was this article helpful?
        </p>

        <div className="flex justify-center gap-4">
          <button
            onClick={() => handleReaction(true)}
            disabled={status === "submitting"}
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-100 text-green-700 rounded-lg font-medium hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ThumbsUp className="w-5 h-5" />
            Yes, helpful
          </button>

          <button
            onClick={() => {
              if (showFeedback) {
                handleReaction(false);
              } else {
                setShowFeedback(true);
              }
            }}
            disabled={status === "submitting"}
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ThumbsDown className="w-5 h-5" />
            Not really
          </button>
        </div>

        {showFeedback && (
          <div className="mt-6 max-w-md mx-auto animate-in fade-in slide-in-from-top-2 duration-300">
            <label className="block text-sm text-gray-600 mb-2 text-left">
              How can we improve this article?
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Your feedback helps us create better content..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent resize-none"
              rows={3}
            />
            <button
              onClick={() => handleReaction(false)}
              disabled={status === "submitting"}
              className="mt-3 w-full px-4 py-2 bg-theme-secondary text-white rounded-lg font-medium hover:bg-theme-secondary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === "submitting" ? "Submitting..." : "Submit Feedback"}
            </button>
          </div>
        )}

        {status === "error" && (
          <p className="mt-4 text-red-600 text-sm">
            Something went wrong. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}

export default BlogReaction;
